import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const SpotlightCard = ({ 
  children, 
  className = "", 
  spotlightColor = "rgba(255, 255, 255, 0.08)",
  tiltIntensity = 8,
  glare = true,
  onClick,
  ...props 
}) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  // Mouse position within card
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth springs for 3D rotation
  const springConfig = { damping: 20, stiffness: 200, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Map mouse position to tilt angles (invert X for natural perspective tilt)
  const rotateX = useTransform(smoothY, [-0.5, 0.5], [tiltIntensity, -tiltIntensity]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-tiltIntensity, tiltIntensity]);

  // Pixel coordinates for spotlight gradient
  const [pixelPos, setPixelPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    
    // Normalized [-0.5, 0.5] coordinates for tilt
    const normX = (e.clientX - rect.left) / rect.width - 0.5;
    const normY = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(normX);
    mouseY.set(normY);

    // Absolute pixels for spotlight highlight
    setPixelPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      className={`relative overflow-hidden group transition-shadow duration-300 ${className}`}
      {...props}
    >
      {/* Dynamic Cursor Spotlight Overlay */}
      <div
        className="pointer-events-none absolute -inset-px transition-opacity duration-300 opacity-0 group-hover:opacity-100 z-10"
        style={{
          background: `radial-gradient(550px circle at ${pixelPos.x}px ${pixelPos.y}px, ${spotlightColor}, transparent 70%)`,
        }}
      />

      {/* Dynamic Border Glow */}
      <div
        className="pointer-events-none absolute -inset-px rounded-[inherit] transition-opacity duration-300 opacity-0 group-hover:opacity-100 z-10 border border-sky-400/40"
        style={{
          maskImage: `radial-gradient(350px circle at ${pixelPos.x}px ${pixelPos.y}px, black 30%, transparent 80%)`,
          WebkitMaskImage: `radial-gradient(350px circle at ${pixelPos.x}px ${pixelPos.y}px, black 30%, transparent 80%)`,
        }}
      />

      {/* Glare effect */}
      {glare && (
        <div
          className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-500 opacity-0 group-hover:opacity-15 mix-blend-overlay"
          style={{
            background: `radial-gradient(300px circle at ${pixelPos.x}px ${pixelPos.y}px, rgba(255,255,255,0.8), transparent 75%)`,
          }}
        />
      )}

      {/* Card Content with preserved 3D depth */}
      <div className="relative z-0 h-full flex flex-col" style={{ transform: "translateZ(15px)" }}>
        {children}
      </div>
    </motion.div>
  );
};

export default SpotlightCard;
